/*
 * assets/js/src/galerie.js — galerie v hlavičce detailu programu.
 *
 * ZADÁNÍ MAJITELE (17. 9. 2026, 11:4x, doslova):
 *   „max 2 řady fotek (náhledů). pokud je tam fotek více, u posledního
 *   náhledu bude malé +, a po kliknutí se rozbalí zbylé fotky. Po kliku
 *   na fotku se daná fotka zobrazí ve velkém okně nad náhledy. pokud tam
 *   bude video jako první (stejně jako na terrahunt), tak bude video jako
 *   první a automaticky se spustí, na mobilu tam bude tlačítko play
 *   a thumbnail videa."
 *
 * ---------------------------------------------------------------------
 * CO MODUL DĚLÁ A CO NAOPAK NEDĚLÁ
 * ---------------------------------------------------------------------
 * Dělá: výměnu fotky ve velkém okně, rozbalení třetí a další řady,
 * aktivní náhled (`aria-current`), šipky na klávesnici a nasazení `src`
 * u vlastního MP4.
 *
 * NEDĚLÁ překryv přes stránku. Ten zůstává modulu `lightbox` a váže se
 * na `.zoomable`, tedy na VELKÉ OKNO. Náhledy třídu `zoomable` od 17. 9.
 * nemají — kdyby ji měly, klik by otevřel překryv a výměna na místě, kterou
 * majitel chce, by se nikdy nestihla projevit.
 *
 * BEZ SKRIPTU SE NIC NEZTRÁCÍ. Každý náhled je `<a href>` na fotku v plné
 * velikosti, náhled videa vede na video. Skrývání řad i překryv „+N" jsou
 * v CSS podmíněné `html:not(.no-js)`, takže bez skriptu jsou vidět všechny
 * fotky a žádné „+N" se nekreslí.
 *
 * ---------------------------------------------------------------------
 * PROČ SE ROZBALENÍ NEVRACÍ PŘI VÝMĚNĚ FOTKY
 * ---------------------------------------------------------------------
 * Třída `open` sedí na `.pthumbs` a nikdo ji nesundává. Zabalit mřížku
 * zpátky po kliknutí na fotku by znamenalo, že divák, který si rozbalil
 * dvacátou fotku, po jejím zobrazení ztratí kontext a musí rozbalovat
 * znovu. Je to výslovný požadavek zadání, ne vedlejší efekt.
 *
 * ---------------------------------------------------------------------
 * AUTOPLAY: PRAVIDLA MÍSTO POKUSU
 * ---------------------------------------------------------------------
 * Vzor je `hero-video.js` z terrahunt-2026 (odtud i poučení, které tam
 * stálo 70 MB na jednom detailu): prvek v markupu NEMÁ `src`, takže do
 * rozhodnutí je pro prohlížeč `NETWORK_EMPTY` a nestahuje nic.
 *
 *   - pod 900 px (mobil a malý tablet): `src` se nenasadí vůbec.
 *     Zůstane `poster` a tlačítko play; teprve klepnutí nasadí zdroj,
 *     zapne `controls` a přehraje. Přesně to majitel popsal.
 *   - `prefers-reduced-motion: reduce`: totéž co mobil. Samovolně
 *     běžící smyčka je pohyb, který si uživatel zakázal.
 *   - jinak: `src`, `autoplay`, `muted`, `loop`. Odmítnuté `play()`
 *     se `.catch()`-uje a poster zůstane — prohlížeč má právo říct ne.
 */

/** Šířka, pod kterou se video nikdy nespouští samo. */
const MOBILNI = '(max-width: 899px)';

/**
 * Šířka, pod kterou se sahá po mobilním přepisu (`-m.mp4`).
 *
 * NENÍ TOTÉŽ co `MOBILNI` o řádek výš a schválně je jiná — přesně jako
 * `MOBILE_QUERY` a `NO_AUTOPLAY_QUERY` v `hero-video.js` z terrahunt-2026.
 * Tahle hranice řeší, KTERÝ soubor se použije, když už se něco přehrává;
 * ta druhá řeší, jestli se má přehrávat VŮBEC. Mezi 768 a 899 px tedy
 * běží desktopová vrstva, ale bez samovolného spuštění.
 */
const UZKY_DOTAZ = '(max-width: 767px)';

/** Jak daleko před viewportem se zdroj nasazuje. */
const OKRAJ = '200px';

/** Totéž číslem pro ruční dopočet bez pozorovatele. */
const OKRAJ_PX = 200;

/** Uživatel si vyžádal klid: žádná samovolná smyčka. */
function klidnyRezim() {
	return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function mobil() {
	return !!(window.matchMedia && window.matchMedia(MOBILNI).matches);
}

function uzky() {
	return !!(window.matchMedia && window.matchMedia(UZKY_DOTAZ).matches);
}

/**
 * Přepočítat po změně šířky, po návratu na záložku a po tlačítku zpět.
 *
 * `visibilitychange` a `pageshow` jsou dva okamžiky, kdy prohlížeč
 * přehrávání běžně zastaví a sám od sebe už nic nezkusí (bfcache).
 * Posluchače se věší jednou za život stránky, ne jednou za galerii.
 */
const SROVNAT = [];
let globalniPosluchace = false;

function srovnejVse() {
	for (let i = SROVNAT.length - 1; i >= 0; i--) {
		if (!document.contains(SROVNAT[i].el)) {
			if (SROVNAT[i].io) SROVNAT[i].io.disconnect();
			SROVNAT.splice(i, 1);
			continue;
		}
		SROVNAT[i].sync();
	}
}

function navesGlobalni() {
	if (globalniPosluchace) return;
	globalniPosluchace = true;

	document.addEventListener('visibilitychange', () => {
		if ('visible' === document.visibilityState) srovnejVse();
	});
	window.addEventListener('pageshow', srovnejVse);

	for (const dotaz of [MOBILNI, UZKY_DOTAZ, '(prefers-reduced-motion: reduce)']) {
		if (!window.matchMedia) break;
		const mq = window.matchMedia(dotaz);
		if (mq.addEventListener) mq.addEventListener('change', srovnejVse);
		else if (mq.addListener) mq.addListener(srovnejVse);
	}
}

/**
 * Vymění obsah velkého okna za fotku z náhledu.
 *
 * Velké okno má pevný `aspect-ratio` (16/9 v `components.css`), takže
 * výměna zdroje nemůže posunout stránku — výška je daná šířkou, ne
 * obrázkem. Proto se tu nic nepředměřuje.
 *
 * @param {HTMLElement} okno  `.pmain`, které je zrovna vidět.
 * @param {HTMLElement} nahled Náhled, na který se klepnulo.
 */
function ukazFotku(okno, nahled) {
	const img = okno.querySelector('img');
	if (!img) return;

	const src = nahled.getAttribute('data-za-src') || nahled.getAttribute('href') || '';
	const srcset = nahled.getAttribute('data-za-srcset') || '';
	const sizes = nahled.getAttribute('data-za-sizes') || '';
	const popis = nahled.getAttribute('aria-label') || '';

	/* Pořadí je schválně: nejdřív pryč se starou sadou, teprve pak nová.
	   Opačně by prohlížeč na okamžik vybíral z `srcset` staré fotky. */
	img.removeAttribute('srcset');
	img.removeAttribute('sizes');
	img.src = src;
	if (srcset) {
		img.srcset = srcset;
		if (sizes) img.sizes = sizes;
	}
	img.alt = popis;
	/* Velké okno je od téhle chvíle odkaz na plnou velikost té fotky. */
	const plna = nahled.getAttribute('href');
	if (plna) okno.setAttribute('href', plna);
}

/**
 * Obsluha jedné galerie.
 *
 * @param {HTMLElement} koren `.pmedia[data-za-galerie]`.
 * @returns {number} 1, když se modul opravdu zapojil.
 */
function setup(koren) {
	const mriz = koren.querySelector('.pthumbs');
	if (!mriz) return 0;

	const nahledy = [...mriz.querySelectorAll('.pthumb')];
	if (nahledy.length < 2) return 0;

	/*
	 * VELKÉ OKNO MŮŽE BÝT VE DVOU PODOBÁCH a šablona vypisuje právě
	 * jednu z nich:
	 *   `.pvid`                  vlastní MP4 (přehrávač v <div>)
	 *   `a.pmain[data-za-embed]` fasáda YouTube/Vimeo (poster + play)
	 *   `a.pmain` bez `embed`    první fotka galerie (program bez videa)
	 * První dvě jsou VIDEO, třetí je FOTKA.
	 */
	const oknoVideo = koren.querySelector('.pvid, a.pmain[data-za-embed]');
	const video = oknoVideo ? oknoVideo.querySelector('video') : null;
	const play = oknoVideo ? oknoVideo.querySelector('button.pplay') : null;

	/*
	 * Jakmile divák sám klepne na play, přestává být video „skrytá
	 * podlimitní varianta", i když šířka pořád odpovídá mobilu. Bez
	 * tohohle příznaku by první další přepočet (návrat na záložku,
	 * otočení telefonu) uvolnil zdroj a přehrávání, které si návštěvník
	 * právě spustil, by tiše zastavil. Táž past je ošetřená v
	 * `hero-video.js` z terrahunt-2026.
	 */
	let rucneSpusteno = false;

	/*
	 * VIDEO A FOTKA JSOU DVA UZLY, NE JEDEN PŘEPÍNANÝ.
	 * Přehrávač ani fasádu s `data-za-embed` nejde „přemalovat" na fotku:
	 * u přehrávače by se ztratila pozice, u fasády by na obrázku zůstal
	 * odkaz do přehrávače. Když je video, doplní se vedle něj skrytá
	 * fotková dlaždice — a protože vzniká PŘED `lightbox.init()`
	 * (pořadí v `index.js`), překryv ji uvidí jako kterýkoli `.zoomable`.
	 */
	let oknoFotky = oknoVideo ? null : koren.querySelector('a.pmain');
	if (oknoVideo) {
		oknoFotky = document.createElement('a');
		oknoFotky.className = 'pmain zoomable';
		oknoFotky.hidden = true;
		const img = document.createElement('img');
		img.decoding = 'async';
		img.alt = '';
		oknoFotky.appendChild(img);
		oknoVideo.insertAdjacentElement('afterend', oknoFotky);
	}

	function prepni(naVideo) {
		if (!oknoVideo) return;
		oknoVideo.hidden = !naVideo;
		if (oknoFotky) oknoFotky.hidden = naVideo;
		if (!naVideo && video && typeof video.pause === 'function') video.pause();
		if (naVideo && video) nasadZdroj(true);
	}

	/**
	 * Která adresa patří do `src` při současné šířce.
	 *
	 * Mobilní přepis (`-m.mp4`) existuje jen u programů, kde ho dávka
	 * vyrobila — server ho vypíše po `file_exists()`, takže se tu neřeší
	 * „existuje soubor?", jen „co mi server řekl". Bez něj se použije
	 * desktopový: fail-open, přesně jako v `hero-video.js`.
	 */
	function vyberZdroj() {
		const desktop = oknoVideo.getAttribute('data-za-video-src') || '';
		const mobilni = oknoVideo.getAttribute('data-za-video-src-mobil') || '';

		return (uzky() && mobilni) ? mobilni : (desktop || mobilni);
	}

	/**
	 * Vrátí prvek do `NETWORK_EMPTY` — prohlížeč pak o žádném souboru neví.
	 *
	 * Volá se po překročení hranice 899 px směrem dolů (otočení telefonu,
	 * zmenšení okna). Bez toho by prvek dál držel stažený buffer a chování
	 * by se rozešlo s tím, co platí po načtení stránky na téže šířce.
	 */
	function uvolniZdroj() {
		if (!video || !video.getAttribute('src')) return;
		video.pause();
		video.autoplay = false;
		video.removeAttribute('src');
		video.load();
		if (play) play.hidden = false;
	}

	/**
	 * Je prvek dost blízko viewportu, aby se zdroj vyplatilo nasadit?
	 *
	 * Ruční dopočet je záloha pro prohlížeč bez `IntersectionObserver`
	 * a pro okamžik hned po překročení hranice, kdy pozorovatel ještě
	 * nestihl ohlásit první průsečík.
	 */
	function vDosahu() {
		const r = oknoVideo.getBoundingClientRect();
		if (0 === r.width && 0 === r.height) return false;

		return r.top < (window.innerHeight + OKRAJ_PX) && r.bottom > -OKRAJ_PX;
	}

	/** Nasadí `src` MP4 podle pravidel výš. `rucne` = divák klepl na play. */
	function nasadZdroj(rucne) {
		if (!video) return;

		if (!rucne) {
			/*
			 * Samovolné přehrávání má tři podmínky a všechny tři se
			 * přepočítávají při každé změně šířky, ne jen jednou po
			 * načtení: nad 899 px, mimo `prefers-reduced-motion` a prvek
			 * v dosahu viewportu. Jakmile jedna padne, zdroj se pustí
			 * z ruky — jinak by po zúžení okna telefon dál táhl desktopovou
			 * stopu, kterou nikdo nevidí.
			 */
			if (rucneSpusteno) return;

			if (mobil() || klidnyRezim()) {
				uvolniZdroj();
				return;
			}

			if (!vDosahu()) return;
		}

		const zdroj = vyberZdroj();
		if (!zdroj) return;

		if (video.getAttribute('src') !== zdroj) {
			if (video.getAttribute('src')) video.pause();
			video.setAttribute('src', zdroj);
			video.load();
		}

		if (rucne) {
			rucneSpusteno = true;
			video.controls = true;
			video.loop = false;
			video.muted = false;
			video.setAttribute('preload', 'metadata');

			/*
			 * JMÉNO AŽ S `controls`. Do téhle chvíle je `<video>` němá
			 * dekorace bez role a `aria-label` by na něm byl podle
			 * specifikace ignorovatelný (a `lint:html` ho hlásí jako
			 * `aria-label-misuse`). Teď je to ovladatelný přehrávač,
			 * takže jméno patří na něj.
			 */
			const popis = video.getAttribute('data-za-video-label');
			if (popis && !video.getAttribute('aria-label')) video.setAttribute('aria-label', popis);

			if (play) play.hidden = true;
		} else {
			video.autoplay = true;
			video.loop = true;
			video.muted = true;
		}

		const p = video.play();
		if (p && typeof p.catch === 'function') p.catch(() => {});
	}

	function aktivuj(nahled) {
		for (const n of nahledy) n.removeAttribute('aria-current');
		nahled.setAttribute('aria-current', 'true');
	}

	function rozbal() {
		mriz.classList.add('open');
		for (const n of nahledy) n.removeAttribute('data-za-more');
	}

	function vyber(nahled) {
		aktivuj(nahled);
		if (nahled.getAttribute('data-za-slot') === 'video') {
			prepni(true);
			return;
		}
		prepni(false);
		if (oknoFotky) ukazFotku(oknoFotky, nahled);
	}

	nahledy.forEach((nahled, k) => {
		nahled.addEventListener('click', (e) => {
			e.preventDefault();
			/* Dokud je mřížka zabalená, je „+N" dlaždice rozbalovač.
			   Po rozbalení je to zase obyčejná fotka. */
			if (nahled.hasAttribute('data-za-more')) {
				rozbal();
				return;
			}
			vyber(nahled);
		});

		nahled.addEventListener('keydown', (e) => {
			if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
			e.preventDefault();
			/* Šipky chodí jen po tom, co je VIDĚT. Skočit na skrytou
			   dlaždici by znamenalo fokus na prvku s `display: none`. */
			const videt = nahledy.filter((n) => n.offsetParent !== null);
			const kde = videt.indexOf(nahled);
			if (kde < 0) return;
			const cil = videt[(kde + (e.key === 'ArrowRight' ? 1 : -1) + videt.length) % videt.length];
			cil.focus();
			if (cil.hasAttribute('data-za-more')) rozbal();
			else vyber(cil);
		});
		void k;
	});

	if (play) {
		play.addEventListener('click', () => nasadZdroj(true));
	}
	if (video) {
		/*
		 * Tlačítko play se skrývá až na UDÁLOST `playing`, ne při nasazení
		 * `src`. Autoplay je žádost, ne příkaz: když ho prohlížeč odmítne
		 * (úsporný režim, zakázané médium), zůstane poster a tlačítko
		 * musí být na svém místě. Řídit to podle vlastního záměru místo
		 * podle stavu přehrávače by nechalo diváka s nehybným obrázkem
		 * a bez čeho kliknout.
		 */
		video.addEventListener('playing', () => {
			play.hidden = true;
		});
		video.addEventListener('pause', () => {
			if (!video.controls) play.hidden = false;
		});

		/* Odmítnutý autoplay při prvním pokusu — zkusit znovu, až je co hrát. */
		video.addEventListener('canplay', () => {
			if (video.paused && video.getAttribute('src') && !video.controls) nasadZdroj(false);
		});

		/*
		 * POZOROVATEL, I KDYŽ JE HRDINA NAHOŘE. Na desktopu sepne prakticky
		 * hned a nic neušetří; smysl má ve chvíli, kdy je detail otevřený
		 * níž (návrat tlačítkem zpět na uloženou pozici) a hlavně jako
		 * pojistka do budoucna — kdyby se blok posunul, chování zůstane
		 * správné bez dalšího zásahu. `200px` znamená „začni, když je
		 * 200 px před viewportem", ne „až když je vidět".
		 */
		const sync = () => nasadZdroj(false);
		let io = null;

		if (window.IntersectionObserver) {
			io = new window.IntersectionObserver((zaznamy) => {
				if (zaznamy.some((z) => z.isIntersecting)) sync();
			}, { rootMargin: OKRAJ });
			io.observe(oknoVideo);
		}

		SROVNAT.push({ el: oknoVideo, sync, io });
		navesGlobalni();

		/*
		 * Bez pozorovatele se rozhoduje hned, ale až v dalším ticku —
		 * měřit rozměry uvnitř `setup()` by u prvku, který se právě
		 * dostal do stromu, vracelo nuly.
		 */
		if (io) sync();
		else window.setTimeout(sync, 0);
	}

	return 1;
}

export function init() {
	const koreny = [...document.querySelectorAll('.pmedia[data-za-galerie]')];
	let pocet = 0;
	for (const koren of koreny) {
		if (koren.dataset.zaGalerieInit === '1') continue;
		koren.dataset.zaGalerieInit = '1';
		pocet += setup(koren);
	}
	return pocet;
}
